/* eslint-disable @typescript-eslint/no-deprecated */

import * as React from 'react'
import styled, { useTheme, css } from 'styled-components'
import { Breakpoint } from '../../styles'

interface GCProps {
  backgroundColor?: string
  columnStart?: number
  stretchY?: boolean
  columnSpan?: number | ColSpans
  customStyles?: ReturnType<typeof css>
}

export const buildColSpans = (columnSpan: number | ColSpans) =>
  typeof columnSpan === 'number' ? { columnSpan, columnSpanTablet: undefined, columnSpanMobile: undefined } : columnSpan

export const GridCard: React.FC<React.PropsWithChildren<GCProps>> = ({
  children,
  backgroundColor,
  columnSpan = 3,
  stretchY,
  columnStart,
  customStyles,
}) => {
  const { colors } = useTheme()

  return (
    <GridCardContainer
      columnStart={columnStart}
      columnSpans={buildColSpans(columnSpan)}
      backgroundColor={backgroundColor ?? colors.white}
      stretchY={stretchY}
      customStyles={customStyles}
    >
      {children}
    </GridCardContainer>
  )
}

interface ColSpans {
  columnSpan: number
  columnSpanTablet?: number
  columnSpanMobile?: number
  columnSpanSmall?: number
}

interface GridCardBackgroundProps {
  columnStart?: number
  columnSpans: ColSpans
  backgroundColor?: string
  stretchY?: boolean
  clickable?: boolean
  customStyles?: ReturnType<typeof css>
}

const ifGtThanReplaceMax = (max: number, n: number) => (n > max ? max : n)

export const GridContainer = styled.div<GridCardBackgroundProps>`
  grid-column: ${(p) => (p.columnStart ? `${p.columnStart} / ` : '')} span ${(p) => p.columnSpans.columnSpan};
  align-self: ${(p) => (p.stretchY ? 'stretch' : 'start')};

  @media (max-width: ${Breakpoint.tablet}) {
    grid-column: span ${(p) => p.columnSpans.columnSpanTablet ?? ifGtThanReplaceMax(4, p.columnSpans.columnSpan)};
  }
  @media (max-width: ${Breakpoint.mobile}) {
    grid-column: span
      ${(p) =>
        p.columnSpans.columnSpanMobile ??
        p.columnSpans.columnSpanTablet ??
        ifGtThanReplaceMax(3, p.columnSpans.columnSpan)};
  }
  @media (max-width: ${Breakpoint.small}) {
    grid-column: span
      ${(p) =>
        p.columnSpans.columnSpanSmall ??
        p.columnSpans.columnSpanMobile ??
        p.columnSpans.columnSpanTablet ??
        ifGtThanReplaceMax(3, p.columnSpans.columnSpan)};
  }
  ${(p) => (p.backgroundColor ? `background-color: ${p.backgroundColor}` : '')};
`

export const GridCardContainer = styled(GridContainer)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 100%;
  box-shadow: 0 0 1rem 0 rgba(0, 0, 0, 0.16);
  border-radius: 2rem;
  outline: none;
  ${(p) => p.customStyles}
  &:hover {
    ${({ clickable }) => (clickable ? 'box-shadow: 0 0 1rem 0 rgb(0 0 0 / 32%);' : '')}
  }
`

export const CardGrid = styled.div<{ dense?: boolean }>`
  display: grid;
  grid-row-gap: 2rem;
  grid-column-gap: 2rem;
  grid-template-columns: repeat(auto-fit, minmax(auto, 15rem)); //15.2rem = 460/3
  ${({ dense }) => (dense ? 'grid-auto-flow: dense;' : '')}
  justify-items: stretch;
  @media (max-width: ${Breakpoint.mobile}) {
    grid-template-columns: 1fr 1fr 0;
    grid-row-gap: 2rem;
    grid-column-gap: 0;
  }
  @media (max-width: ${Breakpoint.small}) {
    grid-template-columns: 1fr 0 0;
    grid-row-gap: 2rem;
    grid-column-gap: 0;
  }
`
